import { createReducer, on } from '@ngrx/store';

import { ProfileActions } from './profile.actions';
import { ProfileState, initialProfileState } from './profile.state';

export const profileReducer = createReducer(
  initialProfileState,

  // API Keys list
  on(
    ProfileActions.loadKeys,
    (state): ProfileState => ({
      ...state,
      apiKeysLoading: true,
      apiKeysError: null
    })
  ),
  on(
    ProfileActions.loadKeysSuccess,
    (state, { keys, cursorNextPage, cursorPrevPage }): ProfileState => ({
      ...state,
      apiKeys: keys,
      cursorNextPage,
      cursorPrevPage,
      apiKeysLoading: false
    })
  ),
  on(
    ProfileActions.loadKeysFailure,
    (state, { error }): ProfileState => ({
      ...state,
      apiKeysLoading: false,
      apiKeysError: error
    })
  ),

  // Single API Key
  on(
    ProfileActions.loadKey,
    (state): ProfileState => ({
      ...state,
      selectedApiKey: null,
      selectedApiKeyLoading: true,
      selectedApiKeyError: null
    })
  ),
  on(
    ProfileActions.loadKeySuccess,
    (state, { key }): ProfileState => ({
      ...state,
      selectedApiKey: key,
      selectedApiKeyLoading: false
    })
  ),
  on(
    ProfileActions.loadKeyFailure,
    (state, { error }): ProfileState => ({
      ...state,
      selectedApiKeyLoading: false,
      selectedApiKeyError: error
    })
  ),

  // Create API Key
  on(
    ProfileActions.createKey,
    (state): ProfileState => ({
      ...state,
      apiKeyFormSaving: true,
      apiKeyFormError: null,
      apiKeyFormSuccess: false
    })
  ),
  on(
    ProfileActions.createKeySuccess,
    (state, { apiKeyValue }): ProfileState => ({
      ...state,
      apiKeyFormSaving: false,
      apiKeyFormSuccess: true,
      createdApiKeyValue: apiKeyValue
    })
  ),
  on(
    ProfileActions.createKeyFailure,
    (state, { error }): ProfileState => ({
      ...state,
      apiKeyFormSaving: false,
      apiKeyFormError: error
    })
  ),

  // Update API Key
  on(
    ProfileActions.updateKey,
    (state): ProfileState => ({
      ...state,
      apiKeyFormSaving: true,
      apiKeyFormError: null,
      apiKeyFormSuccess: false
    })
  ),
  on(
    ProfileActions.updateKeySuccess,
    (state, { key }): ProfileState => ({
      ...state,
      apiKeyFormSaving: false,
      apiKeyFormSuccess: true,
      selectedApiKey: key,
      apiKeys: state.apiKeys.map((k) => (k.id === key.id ? key : k))
    })
  ),
  on(
    ProfileActions.updateKeyFailure,
    (state, { error }): ProfileState => ({
      ...state,
      apiKeyFormSaving: false,
      apiKeyFormError: error
    })
  ),

  // Delete API Key
  on(
    ProfileActions.deleteKey,
    (state): ProfileState => ({
      ...state,
      apiKeyFormSaving: true
    })
  ),
  on(
    ProfileActions.deleteKeySuccess,
    (state, { keyId }): ProfileState => ({
      ...state,
      apiKeyFormSaving: false,
      apiKeys: state.apiKeys.filter((k) => k.id !== keyId)
    })
  ),
  on(
    ProfileActions.deleteKeyFailure,
    (state, { error }): ProfileState => ({
      ...state,
      apiKeyFormSaving: false,
      apiKeyFormError: error
    })
  ),

  // Clear created key value
  on(
    ProfileActions.clearCreatedKeyValue,
    (state): ProfileState => ({
      ...state,
      createdApiKeyValue: null
    })
  ),

  // Reset form
  on(
    ProfileActions.resetForm,
    (state): ProfileState => ({
      ...state,
      apiKeyFormSaving: false,
      apiKeyFormError: null,
      apiKeyFormSuccess: false,
      createdApiKeyValue: null
    })
  ),

  // Password
  on(
    ProfileActions.changePassword,
    (state): ProfileState => ({
      ...state,
      passwordChanging: true,
      passwordChangeError: null,
      passwordChangeSuccess: false
    })
  ),
  on(
    ProfileActions.changePasswordSuccess,
    (state): ProfileState => ({
      ...state,
      passwordChanging: false,
      passwordChangeSuccess: true
    })
  ),
  on(
    ProfileActions.changePasswordFailure,
    (state, { error }): ProfileState => ({
      ...state,
      passwordChanging: false,
      passwordChangeError: error
    })
  ),

  // TOTP Status
  on(
    ProfileActions.loadTotpStatus,
    (state): ProfileState => ({
      ...state,
      totpStatusLoading: true,
      totpError: null
    })
  ),
  on(
    ProfileActions.loadTotpStatusSuccess,
    (state, { enabled }): ProfileState => ({
      ...state,
      totpStatus: enabled,
      totpStatusLoading: false
    })
  ),
  on(
    ProfileActions.loadTotpStatusFailure,
    (state, { error }): ProfileState => ({
      ...state,
      totpStatusLoading: false,
      totpError: error
    })
  ),

  // TOTP Disable
  on(
    ProfileActions.disableTotp,
    (state): ProfileState => ({
      ...state,
      totpDisabling: true,
      totpError: null
    })
  ),
  on(
    ProfileActions.disableTotpSuccess,
    (state): ProfileState => ({
      ...state,
      totpDisabling: false,
      totpStatus: false
    })
  ),
  on(
    ProfileActions.disableTotpFailure,
    (state, { error }): ProfileState => ({
      ...state,
      totpDisabling: false,
      totpError: error
    })
  ),

  // Passkeys
  on(
    ProfileActions.loadPasskeys,
    (state): ProfileState => ({
      ...state,
      passkeysLoading: true,
      passkeysError: null
    })
  ),
  on(
    ProfileActions.loadPasskeysSuccess,
    (state, { passkeys }): ProfileState => ({
      ...state,
      passkeys,
      passkeysLoading: false
    })
  ),
  on(
    ProfileActions.loadPasskeysFailure,
    (state, { error }): ProfileState => ({
      ...state,
      passkeysLoading: false,
      passkeysError: error
    })
  ),

  on(
    ProfileActions.deletePasskey,
    (state, { id }): ProfileState => ({
      ...state,
      passkeyDeleting: id,
      passkeysError: null
    })
  ),
  on(
    ProfileActions.deletePasskeySuccess,
    (state, { id }): ProfileState => ({
      ...state,
      passkeyDeleting: null,
      passkeys: state.passkeys.filter((p) => p.id !== id)
    })
  ),
  on(
    ProfileActions.deletePasskeyFailure,
    (state, { error }): ProfileState => ({
      ...state,
      passkeyDeleting: null,
      passkeysError: error
    })
  ),

  // Profile name
  on(
    ProfileActions.updateProfileName,
    (state): ProfileState => ({
      ...state,
      profileNameSaving: true,
      profileNameSaveError: null,
      profileNameSaveSuccess: false
    })
  ),
  on(
    ProfileActions.updateProfileNameSuccess,
    (state): ProfileState => ({
      ...state,
      profileNameSaving: false,
      profileNameSaveSuccess: true
    })
  ),
  on(
    ProfileActions.updateProfileNameFailure,
    (state, { error }): ProfileState => ({
      ...state,
      profileNameSaving: false,
      profileNameSaveError: error
    })
  ),
  on(
    ProfileActions.resetProfileNameForm,
    (state): ProfileState => ({
      ...state,
      profileNameSaving: false,
      profileNameSaveError: null,
      profileNameSaveSuccess: false
    })
  )
);
